import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { CourseQuiz } from '../components/CourseQuiz';
import { ExerciseSection } from '../components/ExerciseSection';
import { useCourseContent } from '../hooks/useContentData';
import { GrammatikSection } from '../components/GrammatikSection';
import { CourseModuleRenderer } from '../components/CourseModuleRenderer';
import { InteractiveBookViewer } from '../components/InteractiveBookViewer';
import { A1_2_ANSWERS, A1_2_SECTIONS } from '../data/quizBank';
import EXERCISES_A12 from '../data/exerciseBankA12';
import '../styles/menschen-course.css';

const BOOKS_A12 = {
  kursbuch: { id: 'menschen_a12_kursbuch', label: 'Kursbuch', labelEn: 'Textbook', url: 'https://educap01.datenbank-bildungsmedien.net/player/f1e3e1d4fba58a3042f7f61ba084c99c/edureader/app/pdf/lib/web/viewer.html' },
  arbeitsbuch: { id: 'menschen_a12_arbeitsbuch', label: 'Arbeitsbuch', labelEn: 'Workbook', url: 'https://educap01.datenbank-bildungsmedien.net/player/84eb93f3ae22bd3a931c9114c9e9fed9/edureader/app/pdf/lib/web/viewer.html' },
};

const TABS = [
  { id: 'mod5', label: 'Modul 5', icon: '📘', isHeader: true },
  { id: 'l13', label: 'L13: Kleidung', icon: '👕' },
  { id: 'l14', label: 'L14: Feste', icon: '🎊' },
  { id: 'l15', label: 'L15: Alltag', icon: '🏃' },
  { id: 'g5', label: 'Grammatik 13-15', icon: '📐', isGrammatik: true },
  { id: 'mod6', label: 'Modul 6', icon: '📗', isHeader: true },
  { id: 'l16', label: 'L16: Schule', icon: '🏫' },
  { id: 'l17', label: 'L17: Wohnen', icon: '🏢' },
  { id: 'l18', label: 'L18: Gesundheit', icon: '🏥' },
  { id: 'g6', label: 'Grammatik 16-18', icon: '📐', isGrammatik: true },
  { id: 'mod7', label: 'Modul 7', icon: '📙', isHeader: true },
  { id: 'l19', label: 'L19: Kontakte', icon: '📱' },
  { id: 'l20', label: 'L20: Aussehen', icon: '👤' },
  { id: 'l21', label: 'L21: Reisen', icon: '✈️' },
  { id: 'g7', label: 'Grammatik 19-21', icon: '📐', isGrammatik: true },
  { id: 'mod8', label: 'Modul 8', icon: '📕', isHeader: true },
  { id: 'l22', label: 'L22: Arbeit', icon: '💼' },
  { id: 'l23', label: 'L23: Medien', icon: '📺' },
  { id: 'l24', label: 'L24: Zukunft', icon: '🌟' },
  { id: 'g8', label: 'Grammatik 22-24', icon: '📐', isGrammatik: true },
  { id: 'pdf', label: 'Interaktives Lernen', icon: '📖', isPdf: true },
  { id: 'exercises', label: 'Übungen', icon: '🏋️', isExercise: true },
  { id: 'quiz', label: 'Quiz', icon: '✅' },
];

const GRAM_MAP = { g5: ['l13','l14','l15'], g6: ['l16','l17','l18'], g7: ['l19','l20','l21'], g8: ['l22','l23','l24'] };

export default function MenschenA12() {
  const [activeTab, setActiveTab] = useState('l13');
  const { lessons, grammatik: grammatikA12, loading } = useCourseContent('menschen_a12');

  const lessonMap = {
    l13: lessons['lektion13'], l14: lessons['lektion14'], l15: lessons['lektion15'],
    l16: lessons['lektion16'], l17: lessons['lektion17'], l18: lessons['lektion18'],
    l19: lessons['lektion19'], l20: lessons['lektion20'], l21: lessons['lektion21'],
    l22: lessons['lektion22'], l23: lessons['lektion23'], l24: lessons['lektion24'],
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="menschen-a12-page">
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-link">
            <ChevronLeft className="h-5 w-5" /><span className="text-sm">Back</span>
          </Link>
          <h1 className="text-indigo-500 font-bold">Menschen A1.2</h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="course-container">
        <div className="course-header">
          <h1 style={{ color: '#6366f1' }}>Menschen A1.2</h1>
          <p>German for Beginners – Part 2 · <span style={{ color: '#6366f1' }}>الألمانية للمبتدئين – الجزء الثاني</span></p>
          <div className="modul-badges">
            <span className="modul-badge" style={{ background: 'rgba(99,102,241,0.2)', color: '#a5b4fc' }}>Modul 5–8</span>
            <span className="modul-badge" style={{ background: 'rgba(34,197,94,0.2)', color: '#4ade80' }}>12 Lektionen</span>
            <span className="modul-badge" style={{ background: 'rgba(245,158,11,0.2)', color: '#fbbf24' }}>A1.2 Level</span>
          </div>
        </div>

        <div className="tab-nav" data-testid="tab-navigation">
          {TABS.map(tab => tab.isHeader ? (
            <span key={tab.id} className="tab-btn module-header">{tab.icon} {tab.label}</span>
          ) : (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-btn-${tab.id}`}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''} ${tab.isGrammatik ? 'grammatik-tab' : ''} ${tab.isPdf ? 'pdf-tab' : ''} ${tab.isExercise ? 'exercise-tab' : ''}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            <span className="ml-3 text-slate-400">Loading content...</span>
          </div>
        ) : (
          <>
            {Object.entries(lessonMap).map(([key, data]) => (
              data && <CourseModuleRenderer key={key} tabId={key} moduleData={data} isActive={activeTab === key} accentColor="#6366f1" />
            ))}

            {Object.entries(GRAM_MAP).map(([gId, lids]) => (
              <div key={gId} className={`section ${activeTab === gId ? 'active' : ''}`} data-testid={`tab-${gId}`}>
                {lids.map(lid => grammatikA12[lid] && <GrammatikSection key={lid} data={grammatikA12[lid]} isActive={activeTab === gId} />)}
              </div>
            ))}

            <InteractiveBookViewer books={BOOKS_A12} courseTitle="Menschen A1.2" isActive={activeTab === 'pdf'} />

            <div className={`section ${activeTab === 'quiz' ? 'active' : ''}`} data-testid="tab-quiz">
              <CourseQuiz quizId="menschen_a12_quiz" courseId="menschen_a12" title="Menschen A1.2 – Abschlussquiz" sections={A1_2_SECTIONS} answers={A1_2_ANSWERS} isActive={true} />
            </div>

            {activeTab === 'exercises' && (
              <div className="section active" data-testid="tab-exercises">
                <ExerciseLessonPicker exercises={EXERCISES_A12} exerciseKeys={['L13','L14','L15','L16','L17','L18','L19','L20','L21','L22','L23','L24']} labels={['L13: Kleidung','L14: Feste','L15: Alltag','L16: Schule','L17: Wohnen','L18: Gesundheit','L19: Kontakte','L20: Aussehen','L21: Reisen','L22: Arbeit','L23: Medien','L24: Zukunft']} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ExerciseLessonPicker({ exercises, exerciseKeys, labels }) {
  const [selectedLesson, setSelectedLesson] = useState(exerciseKeys[0]);
  return (
    <div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px' }}>Übungen / Exercises</h2>
      <div className="flex flex-wrap gap-2 mb-4">
        {exerciseKeys.map((key, i) => (
          <button key={key} onClick={() => setSelectedLesson(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedLesson === key ? 'bg-teal-600 text-white' : 'bg-muted/50 text-muted-foreground hover:bg-muted border border-border'}`}
            data-testid={`exercise-lesson-${key}`}>{labels[i]}</button>
        ))}
      </div>
      <ExerciseSection lessonId={selectedLesson} isActive={true} exercises={exercises} />
    </div>
  );
}
